// src/resources/sections/sshConfig.ts
import type { ResourceConfig } from "../resourceTypes";

export const SSH_CONFIG: ResourceConfig[] = [
  {
    name: "ssh_credentials_safe",
    label: "SSH Credentials",
    schema: "ssh",
    fields: [
      {
        source: "cred_name",
        label: "Credential name",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "username",
        label: "Username",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "auth_method",
        label: "Auth method",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "password_present",
        label: "Password present",
        kind: "boolean",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "ssh_key_present",
        label: "SSH key present",
        kind: "boolean",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "agent_certificate_present",
        label: "Agent cert present",
        kind: "boolean",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "created_at",
        label: "Created at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "Updated at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    readOnly: true,
    searchFields: ["cred_name", "username"],
    actions: [
      {
        kind: "rpc",
        label: "Ensure Keys",
        rpcName: "ensure_ssh_keys_for_credential",
        method: "POST",
        sendRecord: true,
      },
      {
        kind: "rpc",
        label: "Rotate ed25519",
        rpcName: "rotate_ssh_keypair",
        method: "POST",
        sendRecord: true,
      },
      {
        kind: "rpc",
        label: "Rotate rsa4096",
        rpcName: "rotate_ssh_keypair",
        method: "POST",
        sendRecord: true,
      },
    ],
  },
  {
    name: "certificate_authorities_safe",
    label: "SSH CAs - admin-only",
    schema: "ssh",
    fields: [
      { source: "name", label: "Name", kind: "text", list: true, form: true },
      {
        source: "ca_public_key",
        label: "CA public key",
        kind: "longtext",
        list: false,
        form: false,
        editDisabled: true,
      },
      {
        source: "created_at",
        label: "Created at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "Updated at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    readOnly: true,
    searchFields: ["name"],
  },
  {
    name: "host_keys_safe",
    label: "SSH Host Keys",
    schema: "ssh",
    fields: [
      {
        source: "hostname",
        label: "Hostname",
        kind: "text",
        list: true,
        form: true,
      },
      { source: "name", label: "Name", kind: "text", list: true, form: true },
      {
        source: "key_type",
        label: "Key type",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "public_key",
        label: "Public key",
        kind: "longtext",
        list: false,
        form: false,
        editDisabled: true,
      },
      {
        source: "note",
        label: "Note",
        kind: "longtext",
        list: false,
        form: true,
      },
      {
        source: "created_at",
        label: "Created at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "updated_at",
        label: "Updated at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    readOnly: true,
    searchFields: ["hostname", "name", "key_type"],
  },
  {
    name: "host_certs_safe",
    label: "SSH Host Certs",
    schema: "ssh",
    fields: [
      {
        source: "ca_id",
        label: "CA",
        kind: "uuid",
        list: true,
        form: false,
        ref: {
          resource: "certificate_authorities_safe",
          optionText: "name",
          input: "autocomplete",
          schema: "ssh",
        },
        editDisabled: true,
      },
      {
        source: "host_key_id",
        label: "Host key",
        kind: "uuid",
        list: true,
        form: false,
        ref: {
          resource: "host_keys_safe",
          optionText: "name",
          input: "autocomplete",
          schema: "ssh",
        },
        editDisabled: true,
      },
      {
        source: "identity",
        label: "Identity",
        kind: "text",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "principals",
        label: "Principals",
        kind: "text",
        list: false,
        form: false,
        editDisabled: true,
      },
      {
        source: "valid_from",
        label: "Valid from",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "valid_to",
        label: "Valid to",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "cert_text",
        label: "Cert text",
        kind: "longtext",
        list: false,
        form: false,
        editDisabled: true,
      },
      {
        source: "created_at",
        label: "Created at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    readOnly: true,
    searchFields: ["identity"],
  },
  {
    name: "remote_hosts",
    label: "SSH Remote Hosts",
    schema: "ssh",
    fields: [
      {
        source: "hostname",
        label: "Hostname",
        kind: "text",
        list: true,
        form: true,
      },
      {
        source: "port",
        label: "Port",
        kind: "number",
        list: true,
        form: true,
        defaultValue: 22,
      },
      {
        source: "dest_path",
        label: "Destination path",
        kind: "text",
        list: true,
        form: true,
        defaultValue: "/etc/ssh/ssl",
      },
      {
        source: "owner",
        label: "Owner",
        kind: "text",
        list: false,
        form: true,
        defaultValue: "root:root",
      },
      {
        source: "file_mode_key",
        label: "File mode (key)",
        kind: "text",
        list: false,
        form: true,
        defaultValue: "600",
      },
      {
        source: "file_mode_pub",
        label: "File mode (pub)",
        kind: "text",
        list: false,
        form: true,
        defaultValue: "644",
      },
      {
        source: "file_mode_cert",
        label: "File mode (cert)",
        kind: "text",
        list: false,
        form: true,
        defaultValue: "644",
      },
      {
        source: "service_reload",
        label: "Service reload",
        kind: "longtext",
        list: false,
        form: true,
        defaultValue:
          "systemctl reload-or-restart ssh.service || systemctl reload-or-restart ssh.socket || systemctl reload-or-restart sshd.service || systemctl reload-or-restart sshd.socket",
      },
      {
        source: "create_dirs",
        label: "Create dirs",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: true,
      },
      {
        source: "backup_existing",
        label: "Backup existing",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: true,
      },
      {
        source: "use_sudo",
        label: "Use sudo",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: true,
      },
      {
        source: "updated_at",
        label: "Updated at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["hostname", "dest_path"],
  },
  {
    name: "host_key_automation",
    label: "SSH Host Key Automation",
    schema: "ssh",
    fields: [
      {
        source: "remote_host_id",
        label: "Remote host",
        kind: "uuid",
        list: true,
        form: true,
        ref: {
          resource: "remote_hosts",
          optionText: "hostname",
          input: "autocomplete",
          schema: "ssh",
        },
      },
      {
        source: "host_key_id",
        label: "Host key",
        kind: "uuid",
        list: true,
        form: true,
        ref: {
          resource: "host_keys_safe",
          optionText: "name",
          input: "autocomplete",
          schema: "ssh",
        },
      },
      {
        source: "host_cert_id",
        label: "Host cert",
        kind: "uuid",
        list: true,
        form: true,
        ref: {
          resource: "host_certs_safe",
          optionText: "identity",
          input: "autocomplete",
          schema: "ssh",
        },
      },
      {
        source: "cred_id",
        label: "Credential",
        kind: "uuid",
        list: false,
        form: true,
        ref: {
          resource: "credentials_safe",
          optionText: "cred_name",
          input: "autocomplete",
          schema: "ssh",
        },
      },
      {
        source: "dest_alias",
        label: "Dest alias",
        kind: "text",
        list: true,
        form: true,
        defaultValue: "sshd_host_ed25519",
      },
      {
        source: "enabled",
        label: "Enabled",
        kind: "boolean",
        list: true,
        form: true,
        defaultValue: true,
      },
      {
        source: "last_run_at",
        label: "Last run at",
        kind: "date",
        list: true,
        form: false,
        editDisabled: true,
      },
      {
        source: "last_result",
        label: "Last result",
        kind: "longtext",
        list: false,
        form: false,
        editDisabled: true,
      },
      {
        source: "last_error",
        label: "Last error",
        kind: "longtext",
        list: false,
        form: false,
        editDisabled: true,
      },
    ],
    searchFields: ["dest_alias"],
    actions: [
      {
        kind: "rpc",
        label: "Run",
        rpcName: "run_host_key_automation",
        method: "POST",
        sendRecord: true,
      },
    ],
  },
];
